import { apiClient } from './client';
import { extractBlobErrorMessage } from './payments';
import { downloadBlob } from '../utils/downloadBlob';

export type ReportType =
  | 'leads'
  | 'registrations'
  | 'admissions'
  | 'payments'
  | 'visa'
  | 'travel'
  | 'counselor-performance'
  | 'departure'
  | 'arrival'
  | 'pickup'
  | 'university'
  | 'country';

export interface ReportFilters {
  dateFrom?: string;
  dateTo?: string;
  branchId?: number;
  leadStatusId?: number;
  leadSourceId?: number;
  paymentCategoryId?: number;
  paymentStatus?: string;
  applicationStatus?: string;
  visaStatusId?: number;
  universityId?: number;
  intakeId?: number;
  counselorId?: number;
}

const clean = (filters: ReportFilters) =>
  Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== '' && v !== null));

const fetchReport = async (type: ReportType, format: 'csv' | 'xlsx' | 'pdf', filters: ReportFilters): Promise<Blob> => {
  try {
    const response = await apiClient.get(`/reports/${type}`, {
      params: { ...clean(filters), format },
      responseType: 'blob',
      // A large report can take a while to build.
      timeout: 180_000,
    });
    return response.data as Blob;
  } catch (err) {
    throw new Error(await extractBlobErrorMessage(err, 'The report could not be generated. Please try again.'));
  }
};

export const reportFilename = (type: ReportType, format: 'csv' | 'xlsx' | 'pdf') =>
  `${type}-report-${new Date().toISOString().slice(0, 10)}.${format}`;

export const downloadReport = async (type: ReportType, format: 'csv' | 'xlsx', filters: ReportFilters): Promise<void> => {
  downloadBlob(await fetchReport(type, format, filters), reportFilename(type, format));
};

/** The branded PDF, for the in-app viewer (which also downloads these same
 * bytes). */
export const fetchReportPdf = (type: ReportType, filters: ReportFilters) => fetchReport(type, 'pdf', filters);
